import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Flame, ArrowRight } from "lucide-react";
import useAutoRefreshList from "@/hooks/useAutoRefreshList";
import MerchDetailModal from "./MerchDetailModal";
import useShipping, { shippingLine } from "@/hooks/useShipping";
import Price from "./Price";

export default function Merch({ limit, title = "Latest Trending", subtitle = "Built for people who move heavy things.", showViewAll = false }) {
  const { items: allItems } = useAutoRefreshList("/merch");
  const [detail, setDetail] = useState(null);
  const ship = useShipping();
  const items = limit ? allItems.slice(0, limit) : allItems;

  return (
    <section id="merch" data-testid="merch-section" className="py-16 lg:py-24 bg-[#0C2E2B] text-white relative overflow-hidden">
      <div className="absolute inset-0 grain-bg opacity-20 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 font-tactical text-[11px] uppercase tracking-[0.25em] text-[#FFDF00] mb-2">
              {showViewAll && <Flame className="w-3.5 h-3.5" />} Crew Merch
            </div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
              {title}
            </h2>
            <p className="mt-4 text-zinc-400 max-w-2xl">{subtitle}</p>
          </div>
          {showViewAll && (
            <Link to="/merch">
              <Button data-testid="merch-view-all-btn" className="btn-kinetic rounded-md text-xs">
                Show All Merch <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </Link>
          )}
        </div>

        <div className={`grid grid-cols-1 sm:grid-cols-2 ${limit === 3 ? "lg:grid-cols-3" : "lg:grid-cols-4"} gap-6`}>
          {items.map((p, idx) => (
            <article
              key={p.id}
              data-testid={`merch-card-${idx + 1}`}
              className="bg-[#0F3B37] rounded-xl border border-[#1a5850] overflow-hidden card-lift cursor-pointer"
              onClick={() => setDetail(p)}
            >
              <div className="relative bg-zinc-100">
                <img src={p.image} alt={p.title} className="w-full h-64 object-cover" />
                {p.stock <= 5 && (
                  <Badge className="absolute top-3 left-3 bg-[#FFDF00] hover:bg-[#FFDF00] text-[#0C2E2B] text-[10px] font-bold">
                    Only {p.stock} left
                  </Badge>
                )}
              </div>
              <div className="p-5">
                <h3 className="font-display font-black text-lg">{p.title}</h3>
                <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{p.description}</p>
                <div className="mt-3 flex flex-wrap gap-1">
                  {(p.colors || []).slice(0, 4).map((c) => (
                    <span key={c} className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#0C2E2B] text-white/80 border border-white/10">{c}</span>
                  ))}
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <div>
                    <Price p={p} className="text-xl [&>span:first-child]:text-[#FFDF00]" />
                    <div className="text-[10px] text-zinc-400" data-testid={`merch-shipping-line-${idx + 1}`}>{shippingLine(ship)}</div>
                  </div>
                  <Button
                    data-testid={`merch-add-to-cart-btn-${idx + 1}`}
                    onClick={(e) => { e.stopPropagation(); setDetail(p); }}
                    className="btn-kinetic rounded-md text-xs"
                  >
                    View Details
                  </Button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      <MerchDetailModal product={detail} open={!!detail} onClose={() => setDetail(null)} />
    </section>
  );
}
